// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { BottomField } from 'components/molecules/BottomField';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_PAGE,
  LAYOUT_BREAK_POINTS,
  PAGE_SIZE,
  REGEX_OBJECT,
} from 'util/ConstantValues';
import { OperatorType } from 'util/Enums';
import {
  Option,
  PreRegisResource,
  PreRegisSelectedResource,
  ResourcePreRegisFilter,
  SelectedResources,
} from 'util/Types';
import styles from './PreRegisResourceSelect.module.scss';
import { mergeClasses, normalizeNumber } from 'util/commons';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import ResourceTable from './ResourceTable';
import MobileResourceList from './MobileResouceList';
import { useGetWindowSize } from 'util/hooks/useGetWindowSize';
import { usePagination } from 'util/hooks/usePagination';
import { GetListParams } from 'types/api';
import BigNumber from 'bignumber.js';

export type ResourceTableProps = {
  handleCheckAll: () => void;
  filter: ResourcePreRegisFilter;
  setFilter: Dispatch<SetStateAction<ResourcePreRegisFilter>>;
  resourceData: PreRegisResource[];
  siteOptions: Option[];
  materialNameOptions: Option[];
  operatorType: OperatorType;
  displayResourceData: PreRegisResource[];
  selectedResources: SelectedResources;
  isActionDisabled: (item: PreRegisResource) => boolean;
  handleCheckItem: (value: boolean, item: PreRegisResource) => void;
  handleChangeInput: (value: string, item: PreRegisResource) => void;
  isCheckAllDisabled?: boolean;
  allChecked?: boolean;
};

type Props = {
  operatorType: OperatorType;
  resourceData: PreRegisResource[];
  onNext?: (values: PreRegisSelectedResource[]) => void;
  actionDisabled?: boolean;
  className?: string;
  withMobileMode?: boolean;
  actionProps?: {
    selectedResources: SelectedResources;
    setSelectedResources: Dispatch<SetStateAction<SelectedResources>>;
    filter: ResourcePreRegisFilter;
    setFilter: Dispatch<SetStateAction<ResourcePreRegisFilter>>;
  };
};

const PreRegisResourceSelect: React.FC<Props> = ({
  operatorType,
  resourceData,
  onNext,
  actionDisabled,
  className,
  actionProps,
  withMobileMode,
}) => {
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);

  const { t } = useTranslation();
  const [selectedResources, setSelectedResources] = actionProps
    ? [actionProps.selectedResources, actionProps.setSelectedResources]
    : useState<SelectedResources>({});
  const [filter, setFilter] = actionProps
    ? [actionProps.filter, actionProps.setFilter]
    : useState<ResourcePreRegisFilter>({});
  const isFirstTimeSetFilter = useRef(true);

  const { windowWidth } = useGetWindowSize();

  const { siteOptions, materialNameOptions } = useMemo(
    () =>
      resourceData.reduce(
        (
          rs: { siteOptions: Option[]; materialNameOptions: Option[] },
          item
        ) => {
          if (!rs.siteOptions.find(({ value }) => value === item.site.id)) {
            rs.siteOptions.push({
              value: item.site.id,
              label: item.site.name,
            });
          }

          if (
            !rs.materialNameOptions.find(
              ({ value }) => value === item.materialName
            )
          ) {
            rs.materialNameOptions.push({
              value: item.materialName,
              label: item.materialName,
            });
          }

          return rs;
        },
        { siteOptions: [], materialNameOptions: [] }
      ),
    [resourceData]
  );

  const {
    data: { displayResourceData = [] } = {},
    renderPagination,
    triggerFetch,
    pageMeta: {
      receivedDatetime,
      siteIds,
      materialName,
      total,
      take = PAGE_SIZE,
    },
  } = usePagination<
    ResourcePreRegisFilter & Pick<GetListParams, 'page' | 'take'>,
    {
      total: number;
      displayResourceData: PreRegisResource[];
      page: number;
    }
  >({
    defaultPageSize: 100,
    queryParamKeys: ['siteIds', 'materialName', 'receivedDatetime'],
    callback: async ({
      receivedDatetime,
      siteIds,
      materialName,
      page = DEFAULT_PAGE,
      take = PAGE_SIZE,
    }) =>
      new Promise((res) => {
        const filteredData = resourceData.filter((e) => {
          const cvReceivedDatetime = dayjs(e.receivedDatetime).subtract(
            9,
            'hour'
          );

          return (
            (!siteIds?.length || siteIds.includes(e.site.id)) &&
            (receivedDatetime == null ||
              (dayjs(receivedDatetime[0]).isSameOrBefore(
                cvReceivedDatetime,
                'date'
              ) &&
                dayjs(receivedDatetime[1]).isSameOrAfter(
                  cvReceivedDatetime,
                  'date'
                ))) &&
            (materialName == null || materialName === e.materialName)
          );
        });

        return res({
          total: filteredData.length,
          page,
          displayResourceData: filteredData.slice(
            (page - 1) * take,
            page * take
          ),
        });
      }),
  });

  useEffect(() => {
    if (isFirstTimeSetFilter.current && Object.keys(filter).length === 0) {
      let initialReceivedDatetime: [string, string] | undefined;

      if (receivedDatetime) {
        if (typeof receivedDatetime === 'string') {
          initialReceivedDatetime = [
            receivedDatetime,
            dayjs().format('YYYY-MM-DD'),
          ];
        } else {
          initialReceivedDatetime = receivedDatetime;
        }
      }

      setFilter({
        receivedDatetime: initialReceivedDatetime,
        siteIds,
        materialName,
      });

      isFirstTimeSetFilter.current = false;
    }
  }, [receivedDatetime, siteIds, materialName]);

  useEffect(() => {
    if (resourceData.length || displayResourceData.length) {
      triggerFetch({
        ...filter,
        page: DEFAULT_PAGE,
      });
    }
  }, [resourceData, filter]);

  const isCheckAllDisabled = useMemo(() => {
    const hasSomeCheckedData = Object.keys(selectedResources).some(
      (key) =>
        selectedResources[key].checked === true &&
        displayResourceData.some(
          ({ processingObjectId }) => processingObjectId === key
        )
    );

    return (
      actionDisabled ||
      !displayResourceData.length ||
      (!hasSomeCheckedData &&
        displayResourceData.some(
          ({ materialName }) =>
            materialName !== displayResourceData[0].materialName
        ))
    );
  }, [displayResourceData, actionDisabled, selectedResources]);

  const isActionDisabled = (item: PreRegisResource) => {
    const selectedValues = Object.entries(selectedResources).filter(
      ([id, { checked }]) =>
        checked &&
        displayResourceData.find(
          ({ processingObjectId }) => processingObjectId === id
        )
    );

    return (
      actionDisabled ||
      Boolean(
        selectedValues.length &&
          selectedValues[0][1].materialName !== item.materialName
      )
    );
  };

  const unCheckDifferentMaterials = (
    data: SelectedResources,
    materialName: string
  ) => {
    return Object.entries(data).reduce((rs: SelectedResources, [key, val]) => {
      rs[key] = {
        ...val,
        checked: val.materialName === materialName ? val.checked : false,
        hasError: val.materialName === materialName ? val.hasError : false,
      };

      return rs;
    }, {});
  };

  const isSubmitButtonDisabled = useMemo(() => {
    const selectedData = Object.entries(selectedResources).filter(
      ([id, { checked }]) =>
        checked &&
        resourceData.find(({ processingObjectId }) => processingObjectId === id)
    );

    return (
      !selectedData.length ||
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      selectedData.some(([_, { hasError }]) => hasError)
    );
  }, [selectedResources]);

  const handleChangeInput = (value: string, item: PreRegisResource) => {
    if (
      (value && !REGEX_OBJECT.numberWithDot2Decimal.test(value)) ||
      isActionDisabled(item)
    ) {
      return;
    }

    setSelectedResources((prev) => ({
      ...unCheckDifferentMaterials(prev, item.materialName),
      [item.processingObjectId]: {
        weight: value,
        materialName: item.materialName,
        checked: Boolean(value),
        hasError: Number(value) > Number(item.remainingWeight),
      },
    }));
  };

  const handleCheckItem = (value: boolean, item: PreRegisResource) => {
    if (isActionDisabled(item)) {
      return;
    }

    setSelectedResources((prev) => ({
      ...unCheckDifferentMaterials(prev, item.materialName),
      [item.processingObjectId]: {
        weight: prev[item.processingObjectId]?.weight || item.remainingWeight,
        materialName: item.materialName,
        checked: value,
        hasError:
          value &&
          Number(prev[item.processingObjectId]?.weight ?? 0) >
            Number(item.remainingWeight),
      },
    }));
  };

  const checkableData = useMemo(
    () => displayResourceData.filter((item) => !isActionDisabled(item)),
    [displayResourceData, isActionDisabled]
  );

  const allChecked = useMemo(
    () =>
      Object.entries(selectedResources).filter(
        ([id, { checked }]) =>
          checked &&
          checkableData.find(
            ({ processingObjectId }) => processingObjectId === id
          )
      ).length === checkableData.length,
    [checkableData, selectedResources]
  );

  const submittingValues = useMemo(
    () =>
      Object.entries(selectedResources).reduce(
        (
          rs: PreRegisSelectedResource[],
          [id, { weight, checked, materialName }]
        ) => {
          if (
            !checked ||
            !displayResourceData.find(
              ({ processingObjectId }) => processingObjectId === id
            )
          ) {
            return rs;
          }

          return [
            ...rs,
            {
              processingObjectId: id,
              weight,
              materialName,
            },
          ];
        },
        []
      ),
    [selectedResources, displayResourceData]
  );

  const totalWeight = useMemo(
    () =>
      submittingValues
        .reduce((total, { weight }) => total.plus(weight), new BigNumber(0))
        .toString(),
    [submittingValues]
  );

  const handleCheckAll = () => {
    if (isCheckAllDisabled) {
      return;
    }

    setSelectedResources((prev) => {
      return {
        ...unCheckDifferentMaterials(prev, checkableData[0].materialName),
        ...checkableData.reduce((rs: typeof selectedResources, it) => {
          rs[it.processingObjectId] = {
            weight: prev[it.processingObjectId]?.weight || it.remainingWeight,
            materialName: it.materialName,
            checked: !allChecked,
            hasError: allChecked
              ? false
              : Number(prev[it.processingObjectId]?.weight ?? 0) >
                Number(it.remainingWeight),
          };

          return rs;
        }, {}),
      };
    });
  };

  const handleSubmit = () => {
    onNext?.(submittingValues);
  };

  return (
    <div className={mergeClasses(className, styles.preRegistResourceSelect)}>
      <div className={styles.header}>
        <div className={styles.title}>
          {t('pre_regist_resource_select.title')}
        </div>

        <p className={styles.totalWeight}>
          {t('common.total')}:{' '}
          {normalizeNumber({
            value: totalWeight,
            commaSplit: true,
          })}
          kg
        </p>
      </div>

      <div className={styles.form}>
        {withMobileMode && windowWidth <= LAYOUT_BREAK_POINTS.MD ? (
          <MobileResourceList
            handleCheckAll={handleCheckAll}
            filter={filter}
            setFilter={setFilter}
            resourceData={resourceData}
            siteOptions={siteOptions}
            materialNameOptions={materialNameOptions}
            operatorType={operatorType}
            displayResourceData={displayResourceData}
            selectedResources={selectedResources}
            isActionDisabled={isActionDisabled}
            handleCheckItem={handleCheckItem}
            handleChangeInput={handleChangeInput}
            isCheckAllDisabled={isCheckAllDisabled}
            allChecked={allChecked}
            paginationShown={total > take}
          />
        ) : (
          <ResourceTable
            handleCheckAll={handleCheckAll}
            filter={filter}
            setFilter={setFilter}
            resourceData={resourceData}
            siteOptions={siteOptions}
            materialNameOptions={materialNameOptions}
            operatorType={operatorType}
            displayResourceData={displayResourceData}
            selectedResources={selectedResources}
            isActionDisabled={isActionDisabled}
            handleCheckItem={handleCheckItem}
            handleChangeInput={handleChangeInput}
            isCheckAllDisabled={isCheckAllDisabled}
          />
        )}

        <BottomField
          className={mergeClasses({
            [styles.bottomField]: total > take,
          })}
        >
          <div className={styles.bottom}>
            <div className={styles.bottomBtn}>
              <button
                className={styles.btnRequest}
                type="button"
                disabled={isSubmitButtonDisabled || actionDisabled}
                onClick={handleSubmit}
              >
                {t('pre_regist_resource_select.button_next')}
              </button>
            </div>

            {renderPagination()}
          </div>
        </BottomField>
      </div>
    </div>
  );
};

PreRegisResourceSelect.defaultProps = {
  withMobileMode: true,
};

export default PreRegisResourceSelect;
